/**
 * CSV Import/Export utilities with German format support and validation
 */

import { Property, PropertyInputs, LOIInputs } from '../types';
import { DEFAULT_INPUTS } from '../constants';
import { calculateSingleProperty, generateUUID } from '../utils';
import { validatePropertyInputs } from './validators';

// Use semicolon as delimiter for German CSV format
const CSV_DELIMITER = ';';
const CSV_LINE_BREAK = '\r\n';

interface CSVValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface CSVImportResult {
  success: boolean;
  properties: Property[];
  errors: string[];
  warnings: string[];
}

/**
 * Escape special characters in CSV field
 */
const escapeCSVField = (field: string | number | null | undefined): string => {
  if (field === null || field === undefined) {
    return '';
  }
  
  const str = String(field);
  
  // If contains delimiter, quotes or newline, wrap in quotes
  if (str.includes(CSV_DELIMITER) || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    // Escape quotes by doubling them
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
};

/**
 * Format number for German CSV (comma as decimal separator)
 */
const formatNumberForCSV = (num: number | null | undefined): string => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  return num.toString().replace('.', ',');
};

/**
 * Parse number from German CSV format
 */
const parseNumberFromCSV = (str: string): number => {
  if (!str || str.trim() === '') {
    return 0;
  }
  // Replace comma with dot for parsing
  const normalized = str.trim().replace(',', '.');
  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : num;
};

/**
 * Parse CSV line considering quoted fields
 */
const parseCSVLine = (line: string): string[] => {
  const fields: string[] = [];
  let currentField = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === CSV_DELIMITER && !inQuotes) {
      // Field delimiter
      fields.push(currentField.trim());
      currentField = '';
    } else {
      currentField += char;
    }
  }
  
  // Add last field
  fields.push(currentField.trim());
  
  return fields;
};

/**
 * Validate CSV headers
 */
const validateCSVHeaders = (headers: string[]): CSVValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  /* const _expectedHeaders = [
    'Name',
    'Angebotspreis',
    'Wohnfläche',
    'Kaltmiete/m²',
    'Finanzierung %',
    'Zinssatz %',
    'Tilgung %',
    'Hausgeld',
    'Instandhaltung',
    'Verwaltung'
  ]; */
  
  if (headers.length < 7) {
    errors.push(`CSV muss mindestens 7 Spalten haben (gefunden: ${headers.length})`);
  }
  
  // Check for critical headers
  const criticalHeaders = ['Name', 'Angebotspreis', 'Wohnfläche'];
  criticalHeaders.forEach(header => {
    if (!headers.some(h => h.toLowerCase().includes(header.toLowerCase()))) {
      warnings.push(`Empfohlene Spalte "${header}" nicht gefunden`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Export portfolio to CSV with German formatting
 */
export const exportPortfolioToCSV = (portfolio: Property[]): void => {
  try {
    // Create CSV headers
    const headers = [
      'Name',
      'Angebotspreis',
      'Wohnfläche',
      'Kaltmiete/m²',
      'Finanzierung %',
      'Zinssatz %',
      'Tilgung %',
      'Hausgeld',
      'Instandhaltung',
      'Verwaltung',
      'Eigenkapital',
      'Monatl. Cashflow',
      'ROI %',
      'Bruttomietrendite %',
      'Kaufpreisfaktor'
    ];
    
    // Create CSV rows
    const rows = portfolio.map(property => {
      const calc = property.calculations || calculateSingleProperty(property.inputs);
      
      return [
        escapeCSVField(property.name),
        formatNumberForCSV(property.inputs.angebotspreis),
        formatNumberForCSV(property.inputs.wohnflaeche),
        formatNumberForCSV(property.inputs.kaltmieteProQm),
        formatNumberForCSV(property.inputs.finanzierungsanteil),
        formatNumberForCSV(property.inputs.zinssatz),
        formatNumberForCSV(property.inputs.tilgung),
        formatNumberForCSV(property.inputs.hausgeldUmlagefaehig || 0),
        formatNumberForCSV(property.inputs.instandhaltung),
        formatNumberForCSV(property.inputs.verwaltungskosten || 0),
        formatNumberForCSV(calc.eigenkapitalSoll),
        formatNumberForCSV(calc.cashflowMonatlichNachSteuer),
        formatNumberForCSV(calc.eigenkapitalrendite),
        formatNumberForCSV(calc.bruttomietrenditeSoll),
        formatNumberForCSV(calc.kaufpreisFaktor)
      ].join(CSV_DELIMITER);
    });
    
    // Combine headers and rows
    const csvContent = [headers.join(CSV_DELIMITER), ...rows].join(CSV_LINE_BREAK);
    
    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `portfolio_${new Date().toISOString().split('T')[0]}.csv`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting CSV:', error);
    throw new Error('Fehler beim Exportieren der CSV-Datei');
  }
};

/**
 * Import portfolio from CSV with validation
 */
export const importPortfolioFromCSV = async (file: File): Promise<CSVImportResult> => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const properties: Property[] = [];
  
  try {
    // Read file
    const text = await file.text();
    if (!text || text.trim().length === 0) {
      errors.push('CSV-Datei ist leer');
      return { success: false, properties: [], errors, warnings };
    }
    
    // Split into lines
    const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
    
    if (lines.length < 2) {
      errors.push('CSV-Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten');
      return { success: false, properties: [], errors, warnings };
    }
    
    // Parse headers
    const headers = parseCSVLine(lines[0]);
    const headerValidation = validateCSVHeaders(headers);
    
    if (!headerValidation.isValid) {
      errors.push(...headerValidation.errors);
      return { success: false, properties: [], errors, warnings };
    }
    
    warnings.push(...headerValidation.warnings);
    
    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;
      
      try {
        const values = parseCSVLine(line);
        
        // Skip rows with insufficient data
        if (values.length < 3) {
          warnings.push(`Zeile ${i + 1}: Übersprungen (zu wenige Spalten)`);
          continue;
        }
        
        // Parse values with fallbacks
        const inputs: PropertyInputs & LOIInputs = {
          ...DEFAULT_INPUTS,
          propertyName: values[0] || `Immobilie ${i}`,
          angebotspreis: parseNumberFromCSV(values[1]) || DEFAULT_INPUTS.angebotspreis,
          wohnflaeche: parseNumberFromCSV(values[2]) || DEFAULT_INPUTS.wohnflaeche,
          kaltmieteProQm: parseNumberFromCSV(values[3]) || DEFAULT_INPUTS.kaltmieteProQm,
          finanzierungsanteil: parseNumberFromCSV(values[4]) || DEFAULT_INPUTS.finanzierungsanteil,
          zinssatz: parseNumberFromCSV(values[5]) || DEFAULT_INPUTS.zinssatz,
          tilgung: parseNumberFromCSV(values[6]) || DEFAULT_INPUTS.tilgung,
        };
        
        // Add optional fields if present
        if (values.length > 7) {
          inputs.hausgeldUmlagefaehig = parseNumberFromCSV(values[7]) || DEFAULT_INPUTS.hausgeldUmlagefaehig;
        }
        if (values.length > 8) {
          inputs.instandhaltung = parseNumberFromCSV(values[8]) || DEFAULT_INPUTS.instandhaltung;
        }
        if (values.length > 9) {
          inputs.verwaltungskosten = parseNumberFromCSV(values[9]) || DEFAULT_INPUTS.verwaltungskosten;
        }
        
        // Validate inputs
        const validation = validatePropertyInputs(inputs);
        if (!validation.valid) {
          const errorMessages = Object.entries(validation.errors)
            .filter(([_, error]) => error)
            .map(([field, error]) => `${field}: ${error}`);
          
          warnings.push(`Zeile ${i + 1}: Validierungsfehler - ${errorMessages.join(', ')}`);
          
          // Apply corrections where possible
          Object.entries(validation.errors).forEach(([field, _]) => {
            const rules = (validation as any).rules?.[field];
            if (rules) {
              const value = (inputs as any)[field];
              if (typeof value === 'number') {
                if (rules.min !== undefined && value < rules.min) {
                  (inputs as any)[field] = rules.min;
                }
                if (rules.max !== undefined && value > rules.max) {
                  (inputs as any)[field] = rules.max;
                }
              }
            }
          });
        }
        
        // Calculate and create property
        const calculations = calculateSingleProperty(inputs);
        
        properties.push({
          id: generateUUID(),
          name: inputs.propertyName,
          isFavorite: false,
          inputs,
          calculations
        });
        
      } catch (error) {
        warnings.push(`Zeile ${i + 1}: Fehler beim Parsen - ${error}`);
      }
    }
    
    if (properties.length === 0 && warnings.length > 0) {
      errors.push('Keine gültigen Immobilien konnten importiert werden');
      return { success: false, properties: [], errors, warnings };
    }
    
    return {
      success: properties.length > 0,
      properties,
      errors,
      warnings
    };
    
  } catch (error) {
    console.error('Error importing CSV:', error);
    errors.push(`Fehler beim Lesen der Datei: ${error}`);
    return { success: false, properties: [], errors, warnings };
  }
};

/**
 * Generate sample CSV for download
 */
export const generateSampleCSV = (): void => {
  const sampleData = [
    {
      name: 'Beispiel Wohnung München',
      inputs: {
        ...DEFAULT_INPUTS,
        propertyName: 'Beispiel Wohnung München',
        angebotspreis: 450000,
        wohnflaeche: 75,
        kaltmieteProQm: 18,
        finanzierungsanteil: 80,
        zinssatz: 3.5,
        tilgung: 2,
        hausgeld: 250,
        instandhaltung: 50,
        verwaltung: 25
      }
    },
    {
      name: 'Beispiel Wohnung Berlin',
      inputs: {
        ...DEFAULT_INPUTS,
        propertyName: 'Beispiel Wohnung Berlin',
        angebotspreis: 350000,
        wohnflaeche: 65,
        kaltmieteProQm: 15,
        finanzierungsanteil: 75,
        zinssatz: 3.8,
        tilgung: 2.5,
        hausgeld: 200,
        instandhaltung: 40,
        verwaltung: 20
      }
    }
  ];
  
  const sampleProperties: Property[] = sampleData.map(data => ({
    id: generateUUID(),
    name: data.name,
    isFavorite: false,
    inputs: data.inputs as PropertyInputs & LOIInputs,
    calculations: calculateSingleProperty(data.inputs as PropertyInputs & LOIInputs)
  }));
  
  exportPortfolioToCSV(sampleProperties);
};